import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useAxios from '../../helpers/useAxios';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Card from '../../components/Card/Card';
import CardHeader from '../../components/Card/CardHeader';
import CardBody from '../../components/Card/CardBody';
import Loading from '../../components/Loading/Loading';
import { IDeepResearchData, IConversation, IConversationMessage } from '../../constants/types';

const DeepResearch: React.FC = () => {
    console.log('DeepResearch component rendering');

    const [researchData, setResearchData] = useState<IDeepResearchData>({
        conversations: []
    });

    // Set up the useAxios hook for fetching deep research data
    const [{ data, loading, error }, execute] = useAxios<IDeepResearchData>(
        { url: `/dashboard/organization/deep-research` }, 
        { manual: true }
    );

    console.log('useAxios state:', { hasData: !!data, loading, hasError: !!error });

    // Fetch data when the component mounts
    useEffect(() => {
        console.log('Executing deep research API request');
        execute()
            .then(() => console.log('Deep research API request completed'))
            .catch((err) => console.error('Deep research API request failed:', err));
    }, [execute]);

    // Update state when data arrives
    useEffect(() => {
        console.log('Deep research data effect triggered:', { hasData: !!data });

        if (data) {
            console.log('Raw deep research API response:', data);
            setResearchData(data);
        }
    }, [data]);

    // Handle loading state
    if (loading) {
        console.log('Rendering deep research loading state');
        return (
            <div style={{ textAlign: 'center' }}>
                <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                        <Loading />
                    </GridItem>
                </GridContainer>
            </div>
        );
    }

    // Handle error state
    if (error) {
        console.error('Rendering deep research error state:', error);
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <h3>Error loading deep research data</h3>
                <p>{error.message}</p>
            </div>
        );
    }

    console.log('Rendering deep research with data:', {
        conversationCount: researchData.conversations?.length || 0
    });

    return (
        <div style={{ height: '100%', width: '100%' }}>
            <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                    <Card>
                        <CardHeader color="info">
                            <h4 style={{ fontWeight: 'bold' }}>Deep Research Conversations</h4>
                            {researchData.companyName && (
                                <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8, fontWeight: 'bold' }}>
                                    Company: {researchData.companyName}
                                </p>
                            )}
                        </CardHeader>
                        <CardBody>
                            {researchData.conversations && researchData.conversations.length > 0 ? (
                                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    {researchData.conversations.map((conversation: IConversation, conversationIndex: number) => (
                                        <div key={conversation.conversationId} style={{ marginBottom: '30px' }}>
                                            <div style={{ 
                                                backgroundColor: '#f8f9fa', 
                                                padding: '10px', 
                                                borderRadius: '8px', 
                                                marginBottom: '15px',
                                                borderLeft: '4px solid #007bff'
                                            }}>
                                                <h5 style={{ margin: '0 0 5px 0', color: '#007bff' }}>
                                                    Conversation {conversationIndex + 1}
                                                </h5>
                                                <small style={{ color: '#666' }}>
                                                    Created: {new Date(conversation.createdOn).toLocaleString()} | 
                                                    Service: {conversation.llmService} | 
                                                    Version: {conversation.llmVersion}
                                                </small>
                                            </div>
                                            {conversation.messages && conversation.messages.map((message: IConversationMessage, messageIndex: number) => (
                                                <div
                                                    key={message.id || messageIndex}
                                                    style={{
                                                        marginBottom: '15px',
                                                        padding: '10px',
                                                        borderRadius: '8px',
                                                        backgroundColor: message.role === 'user' ? '#f5f5f5' : '#e3f2fd',
                                                        borderLeft: `4px solid ${message.role === 'user' ? '#2196f3' : '#64b5f6'}`
                                                    }}
                                                >
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        justifyContent: 'space-between', 
                                                        alignItems: 'center',
                                                        marginBottom: '5px'
                                                    }}>
                                                        <strong style={{ 
                                                            color: message.role === 'user' ? '#2196f3' : '#64b5f6',
                                                            textTransform: 'capitalize'
                                                        }}>
                                                            {message.role}
                                                        </strong>
                                                        {message.timestamp && (
                                                            <small style={{ color: '#666', fontSize: '12px' }}>
                                                                {new Date(message.timestamp).toLocaleString()}
                                                            </small>
                                                        )}
                                                    </div>
                                                    <div 
                                                        style={{ 
                                                            lineHeight: '1.5',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        {message.role === 'user' ? (
                                                            // Simple text rendering for user messages
                                                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                                                {message.content}
                                                            </div>
                                                        ) : (
                                                            // Rich markdown rendering for assistant messages
                                                            <ReactMarkdown 
                                                                remarkPlugins={[remarkGfm]}
                                                                components={{
                                                                    // Custom heading component to handle company name titles
                                                                    h1: ({node, children, ...props}) => {
                                                                        const text = children?.toString() || '';
                                                                        // Check if this looks like a company name with description
                                                                        if (text.includes(':') && text.length > 50) {
                                                                            const [title, ...descriptionParts] = text.split(':');
                                                                            const description = descriptionParts.join(':').trim();
                                                                            return (
                                                                                <div style={{ margin: '16px 0 8px 0' }}>
                                                                                    <h1 style={{
                                                                                        color: '#2c3e50', 
                                                                                        fontSize: '20px', 
                                                                                        fontWeight: 'bold',
                                                                                        margin: '0 0 8px 0',
                                                                                        lineHeight: '1.3',
                                                                                        borderBottom: '2px solid #3498db',
                                                                                        paddingBottom: '4px'
                                                                                    }}>
                                                                                        {title}:
                                                                                    </h1>
                                                                                    <p style={{
                                                                                        margin: '8px 0',
                                                                                        lineHeight: '1.6',
                                                                                        fontSize: '14px',
                                                                                        color: '#2c3e50'
                                                                                    }}>
                                                                                        {description}
                                                                                    </p>
                                                                                </div>
                                                                            );
                                                                        }
                                                                        // Regular H1 styling for short titles
                                                                        return (
                                                                            <h1 style={{
                                                                                color: '#2c3e50', 
                                                                                fontSize: '20px', 
                                                                                fontWeight: 'bold',
                                                                                margin: '16px 0 8px 0',
                                                                                lineHeight: '1.3',
                                                                                borderBottom: '2px solid #3498db',
                                                                                paddingBottom: '4px'
                                                                            }} {...props}>
                                                                                {children}
                                                                            </h1>
                                                                        );
                                                                    },
                                                                    h2: ({node, ...props}) => (
                                                                        <h2 style={{
                                                                            color: '#34495e', 
                                                                            fontSize: '18px', 
                                                                            fontWeight: 'bold',
                                                                            margin: '14px 0 6px 0',
                                                                            lineHeight: '1.3'
                                                                        }} {...props} />
                                                                    ),
                                                                    h3: ({node, ...props}) => (
                                                                        <h3 style={{
                                                                            color: '#7f8c8d', 
                                                                            fontSize: '16px', 
                                                                            fontWeight: 'bold',
                                                                            margin: '12px 0 4px 0',
                                                                            lineHeight: '1.3'
                                                                        }} {...props} />
                                                                    ),
                                                                    h4: ({node, ...props}) => (
                                                                        <h4 style={{
                                                                            color: '#95a5a6', 
                                                                            fontSize: '15px', 
                                                                            fontWeight: 'bold',
                                                                            margin: '10px 0 3px 0',
                                                                            lineHeight: '1.3'
                                                                        }} {...props} />
                                                                    ),
                                                                    h5: ({node, ...props}) => (
                                                                        <h5 style={{
                                                                            color: '#bdc3c7', 
                                                                            fontSize: '14px', 
                                                                            fontWeight: 'bold',
                                                                            margin: '8px 0 2px 0',
                                                                            lineHeight: '1.3'
                                                                        }} {...props} />
                                                                    ),
                                                                    h6: ({node, ...props}) => (
                                                                        <h6 style={{
                                                                            color: '#ecf0f1', 
                                                                            fontSize: '13px', 
                                                                            fontWeight: 'bold',
                                                                            margin: '6px 0 1px 0',
                                                                            lineHeight: '1.3'
                                                                        }} {...props} />
                                                                    ),
                                                                    p: ({node, ...props}) => (
                                                                        <p style={{
                                                                            margin: '8px 0',
                                                                            lineHeight: '1.6',
                                                                            fontSize: '14px',
                                                                            color: '#2c3e50'
                                                                        }} {...props} />
                                                                    ),
                                                                    strong: ({node, ...props}) => (
                                                                        <strong style={{
                                                                            color: '#2c3e50', 
                                                                            fontWeight: 'bold',
                                                                            fontSize: 'inherit'
                                                                        }} {...props} />
                                                                    ),
                                                                    em: ({node, ...props}) => (
                                                                        <em style={{
                                                                            color: '#7f8c8d', 
                                                                            fontStyle: 'italic',
                                                                            fontSize: 'inherit'
                                                                        }} {...props} />
                                                                    ),
                                                                    ul: ({node, ...props}) => (
                                                                        <ul style={{
                                                                            margin: '8px 0',
                                                                            paddingLeft: '24px',
                                                                            lineHeight: '1.6'
                                                                        }} {...props} />
                                                                    ),
                                                                    ol: ({node, ...props}) => (
                                                                        <ol style={{
                                                                            margin: '8px 0',
                                                                            paddingLeft: '24px',
                                                                            lineHeight: '1.6'
                                                                        }} {...props} />
                                                                    ),
                                                                    li: ({node, ...props}) => (
                                                                        <li style={{
                                                                            margin: '4px 0',
                                                                            lineHeight: '1.6',
                                                                            fontSize: '14px',
                                                                            color: '#2c3e50'
                                                                        }} {...props} />
                                                                    ),
                                                                    blockquote: ({node, ...props}) => (
                                                                        <blockquote style={{
                                                                            borderLeft: '4px solid #3498db',
                                                                            margin: '12px 0',
                                                                            padding: '12px 20px',
                                                                            backgroundColor: '#f8f9fa',
                                                                            fontStyle: 'italic',
                                                                            borderRadius: '0 4px 4px 0',
                                                                            fontSize: '14px',
                                                                            lineHeight: '1.6'
                                                                        }} {...props} />
                                                                    ),
                                                                    code: ({node, inline, ...props}) => 
                                                                        inline ? (
                                                                            <code style={{
                                                                                backgroundColor: '#f1f2f6',
                                                                                padding: '2px 6px',
                                                                                borderRadius: '3px',
                                                                                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                                                                fontSize: '13px',
                                                                                color: '#e74c3c'
                                                                            }} {...props} />
                                                                        ) : (
                                                                            <pre style={{
                                                                                backgroundColor: '#f8f9fa',
                                                                                padding: '16px',
                                                                                borderRadius: '6px',
                                                                                overflow: 'auto',
                                                                                margin: '12px 0',
                                                                                border: '1px solid #e9ecef'
                                                                            }}>
                                                                                <code style={{
                                                                                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                                                                    fontSize: '13px',
                                                                                    lineHeight: '1.4'
                                                                                }} {...props} />
                                                                            </pre>
                                                                        ),
                                                                    table: ({node, ...props}) => (
                                                                        <table style={{
                                                                            borderCollapse: 'collapse',
                                                                            width: '100%',
                                                                            margin: '12px 0',
                                                                            border: '1px solid #ddd',
                                                                            fontSize: '14px'
                                                                        }} {...props} />
                                                                    ),
                                                                    th: ({node, ...props}) => (
                                                                        <th style={{
                                                                            border: '1px solid #ddd',
                                                                            padding: '10px 8px',
                                                                            backgroundColor: '#f2f2f2',
                                                                            fontWeight: 'bold',
                                                                            textAlign: 'left'
                                                                        }} {...props} />
                                                                    ),
                                                                    td: ({node, ...props}) => (
                                                                        <td style={{
                                                                            border: '1px solid #ddd',
                                                                            padding: '10px 8px',
                                                                            lineHeight: '1.4'
                                                                        }} {...props} />
                                                                    ),
                                                                    a: ({node, ...props}) => (
                                                                        <a style={{
                                                                            color: '#3498db',
                                                                            textDecoration: 'underline',
                                                                            fontSize: 'inherit'
                                                                        }} {...props} />
                                                                    )
                                                                }}
                                                            >
                                                                {message.content}
                                                            </ReactMarkdown>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <p>No deep research conversations found for this assessment.</p>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>
        </div>
    );
};

export default DeepResearch; 